import React, { useState } from 'react'

import {
    Button,
    InputAdornment,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { ErrorOutline } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { Container } from '../../components'
import { Concession, CreateConcessionInput } from '../../models/Concession'
import { useConcessions } from '../../hooks/useConcessions'
import { toYYYYMMDD } from '../../helpers/date'
import { computeEndDate, displayLongDate, parseApiDate } from './utils'
import { validateConcession } from './validation'
import { useConcessionStyles } from './styles'

interface Props {
    concession?: Concession
    handleClose: () => void
    onCreated?: (concession: Concession) => void
    defaultActive?: boolean
}

export const ConcessionForm: React.FC<Props> = ({
    concession,
    handleClose,
    onCreated,
    defaultActive = false,
}) => {
    const theme = useTheme()
    const classes = useConcessionStyles()
    const { createConcession, updateConcession } = useConcessions()

    const [name, setName] = useState(concession?.name ?? '')
    const [description, setDescription] = useState(
        concession?.description ?? '',
    )
    const [isActive, setIsActive] = useState(
        concession?.is_active ?? defaultActive,
    )
    const [creditAmount, setCreditAmount] = useState(
        concession?.credit_amount && Number(concession.credit_amount) !== 0
            ? concession.credit_amount
            : '',
    )
    const [startDate, setStartDate] = useState(concession?.start_date ?? '')
    const [durationDays, setDurationDays] = useState(
        concession?.duration_days ? String(concession.duration_days) : '',
    )
    const [errors, setErrors] = useState<string[]>([])

    const isSaving = createConcession.isLoading || updateConcession.isLoading

    const durationNum = Number(durationDays)
    const previewEnd =
        startDate && Number.isInteger(durationNum) && durationNum >= 1
            ? computeEndDate(startDate, durationNum)
            : ''

    const handleSave = () => {
        const validationErrors = validateConcession({
            name,
            description,
            is_active: isActive,
            start_date: startDate,
            duration_days: durationDays,
        })
        if (validationErrors.length > 0) {
            setErrors(validationErrors)
            return
        }
        setErrors([])

        const payload: CreateConcessionInput = {
            name: name.trim(),
            description: description.trim(),
            is_active: isActive,
            credit_amount: creditAmount
                ? Number(creditAmount).toFixed(2)
                : '0.00',
            start_date: startDate,
            duration_days: Number(durationDays),
        }

        if (concession) {
            updateConcession.mutate(
                { id: concession.id, body: payload },
                { onSuccess: () => handleClose() },
            )
        } else {
            createConcession.mutate(payload, {
                onSuccess: (created) => {
                    onCreated?.(created)
                    handleClose()
                },
            })
        }
    }

    const fieldSpacing = { marginBottom: theme.spacing(2), width: '100%' }

    return (
        <>
            <div className={classes.body}>
                {/* Offer details */}
                <div className={classes.section}>
                    <div className={classes.sectionHeader}>
                        <span className={classes.sectionTitle}>Offer</span>
                        <span className={classes.sectionHint}>
                            What tenants get for renewing
                        </span>
                    </div>
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        placeholder="e.g. Renew & Save 2026"
                        style={fieldSpacing}
                    />
                    <TextField
                        label="Description"
                        variant="outlined"
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={5}
                        required
                        placeholder="Describe the offer in your own words — e.g. Renew now and we'll waive your pet fee and throw in free reserved parking for your next term."
                        helperText="This free-text offer is what your messages talk about."
                        style={fieldSpacing}
                    />
                    <TextField
                        label="Monthly credit"
                        variant="outlined"
                        size="small"
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        placeholder="0.00"
                        helperText="Posted as its own credit line on the lease offer. Leave blank for $0."
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    $
                                </InputAdornment>
                            ),
                        }}
                        style={{ ...fieldSpacing, marginBottom: 0 }}
                    />
                </div>

                {/* Schedule & status */}
                <div className={classes.section}>
                    <div className={classes.sectionHeader}>
                        <span className={classes.sectionTitle}>
                            Deadline &amp; status
                        </span>
                        <span className={classes.sectionHint}>
                            Urgency window for the offer
                        </span>
                    </div>
                    <div className={classes.row}>
                        <KeyboardDatePicker
                            label="Start date"
                            format="MM/dd/yyyy"
                            inputVariant="outlined"
                            size="small"
                            value={parseApiDate(startDate)}
                            onChange={(date) =>
                                setStartDate(date ? toYYYYMMDD(date) : '')
                            }
                            style={{ flex: 1 }}
                        />
                        <Container direction="column" style={{ flex: 1 }}>
                            <TextField
                                label="Duration (days)"
                                variant="outlined"
                                size="small"
                                type="number"
                                value={durationDays}
                                onChange={(e) =>
                                    setDurationDays(e.target.value)
                                }
                                inputProps={{ min: 1, step: 1 }}
                                required
                                fullWidth
                            />
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ marginTop: theme.spacing(0.5) }}
                            >
                                {previewEnd
                                    ? `Ends ${displayLongDate(
                                          previewEnd,
                                      )} · ${durationNum} day${
                                          durationNum === 1 ? '' : 's'
                                      }`
                                    : 'Set a start date and duration to see the deadline.'}
                            </Typography>
                        </Container>
                    </div>
                    <div
                        className={classes.toggleCard}
                        style={{ marginTop: theme.spacing(2) }}
                    >
                        <Container direction="column">
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                {isActive ? 'Active' : 'Inactive'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {isActive
                                    ? 'Published and visible to tenants'
                                    : 'Hidden — flip on to publish'}
                            </Typography>
                        </Container>
                        <Switch
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color="primary"
                        />
                    </div>
                </div>

                {errors.length > 0 && (
                    <div className={classes.errorBox}>
                        <ErrorOutline
                            fontSize="small"
                            style={{ color: theme.palette.error.main }}
                        />
                        <Container direction="column">
                            {errors.map((err, i) => (
                                <Typography
                                    key={i}
                                    variant="body2"
                                    style={{ color: theme.palette.error.dark }}
                                >
                                    {err}
                                </Typography>
                            ))}
                        </Container>
                    </div>
                )}
            </div>

            <div className={classes.footer}>
                <Button
                    variant="outlined"
                    size="medium"
                    color="secondary"
                    onClick={handleClose}
                    disabled={isSaving}
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleSave}
                    disabled={isSaving}
                    className={classes.saveButton}
                >
                    {concession ? 'Save Changes' : 'Create Concession'}
                </Button>
            </div>
        </>
    )
}
